import type { ReactNode } from 'react';
import type { ApplicationStatus, FitRecommendation, JobStatus } from '@etnrlz-jobs/shared';

export function Spinner({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      />
    </svg>
  );
}

export function JobStatusBadge({ status }: { status: JobStatus }) {
  const map: Record<JobStatus, { label: string; cls: string }> = {
    draft: { label: 'Rascunho', cls: 'bg-slate-700/60 text-slate-300' },
    open: { label: 'Aberta', cls: 'bg-emerald-900/60 text-emerald-300' },
    closed: { label: 'Encerrada', cls: 'bg-red-900/50 text-red-300' },
  };
  const { label, cls } = map[status];
  return <span className={`badge ${cls}`}>{label}</span>;
}

export function AppStatusBadge({ status }: { status: ApplicationStatus }) {
  const map: Record<ApplicationStatus, { label: string; cls: string }> = {
    new: { label: 'Novo', cls: 'bg-sky-900/60 text-sky-300' },
    reviewed: { label: 'Analisado', cls: 'bg-slate-700/60 text-slate-300' },
    shortlisted: { label: 'Shortlist', cls: 'bg-emerald-900/60 text-emerald-300' },
    rejected: { label: 'Descartado', cls: 'bg-red-900/50 text-red-300' },
  };
  const { label, cls } = map[status];
  return <span className={`badge ${cls}`}>{label}</span>;
}

export function FitScore({ score }: { score: number }) {
  const color =
    score >= 80
      ? 'text-emerald-400 border-emerald-700'
      : score >= 60
        ? 'text-lime-300 border-lime-700'
        : score >= 40
          ? 'text-amber-300 border-amber-700'
          : 'text-red-400 border-red-800';
  return (
    <span
      className={`inline-flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-bold ${color}`}
    >
      {Math.round(score)}
    </span>
  );
}

export function RecommendationBadge({ rec }: { rec: FitRecommendation }) {
  const map: Record<FitRecommendation, { label: string; cls: string }> = {
    advance: { label: 'Entrevistar', cls: 'bg-emerald-900/60 text-emerald-300' },
    maybe: { label: 'Talvez', cls: 'bg-amber-900/50 text-amber-300' },
    reject: { label: 'Não avançar', cls: 'bg-red-900/50 text-red-300' },
  };
  const { label, cls } = map[rec];
  return <span className={`badge ${cls}`}>{label}</span>;
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
      <div>
        <h1 className="text-xl font-bold text-slate-100">{title}</h1>
        {subtitle && <p className="mt-0.5 text-sm text-slate-400">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function ErrorBox({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-red-800 bg-red-950/50 px-4 py-3 text-sm text-red-300">
      {message}
    </div>
  );
}

export const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });

export const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
